import axios, { AxiosResponse } from 'axios';
import { useAuthStore } from '../stores/auth'; // ใช้ relative path ให้ตรงกับโปรเจกต์
import { MANAGER_ENDPOINT } from './site-config';
import { pinia } from './pinia';

export interface HttpOptions {
	headers?: Record<string, string>;
	params?: Record<string, string | number>;
}

export const endpoint = 'https://rpgg168.com/v1/tm/prd';

function getToken(): string | null {
	// อ่านจาก localStorage ตรง ๆ ห้ามผ่าน Pinia: interceptor รันนอก component
	// context, useStore() จึง resolve จาก activePinia ซึ่งเปลี่ยนตัวได้ทุกครั้งที่มี
	// app.use(pinia) ใหม่ — เคยทำให้ request หลุด Authorization → 401 → เด้ง login
	// ทั้งที่ login อยู่. localStorage คือ source of truth ที่ auth store เขียนลงอยู่แล้ว
	try {
		return localStorage.getItem('auth_token');
	} catch {
		return null;
	}
}

const axiosInstance = axios.create({
	baseURL: endpoint,
	headers: {
		Accept: 'application/json',
	},
	timeout: 20000,
});

axiosInstance.interceptors.request.use((config) => {
	const token = getToken();
	if (token) {
		config.headers = config.headers || {};
		config.headers['Authorization'] = `Bearer ${token}`;
	}
	return config;
});

axiosInstance.interceptors.response.use(
	(response: AxiosResponse) => response,
	(error) => {
		const status = error?.response?.status;

		// ระบุ pinia ตรง ๆ ไม่พึ่ง activePinia ที่ popup ตัวล่าสุดอาจยึดไว้
		const auth = useAuthStore(pinia)

		const requestUrl = error?.config?.url || '';

		switch (status) {
			case 400:
				console.warn('Bad Request (400)');
				break;
			case 401:
				console.warn('Unauthorized (401)');
				// ไม่ยุ่งกับ /login และ /logout เอง เพื่อกัน loop — /logout ยิงพร้อม
				// token ที่อาจหมดอายุ ถ้าเด้ง redirect ตรงนี้จะตัดจังหวะ logout ทิ้ง
				if (requestUrl !== '/login' && requestUrl !== '/logout') {
					// redirect เฉพาะตอนที่เพิ่งมี token อยู่ — กันทั้ง 401 หลายเส้นที่ยิง
					// พร้อมกัน (เส้นถัดมาเห็น token เป็น null แล้ว) และกัน loop ตอนไม่มี token
					const hadToken = !!auth.token;
					auth.forceLogout();
					if (hadToken) {
						window.location.href = window.location.origin;
					}
				}
				break;
			case 404:
				console.warn('Not Found (404)');
				break;
			case 500:
				console.error('Server Error (500)');
				break;
		}

		return Promise.reject(error);
	}
);

export const http = {
	async get<T>(path: string, options?: HttpOptions): Promise<T> {
		try {
			const response = await axiosInstance.get<T>(path, {
				headers: options?.headers,
				params: options?.params,
			});
			return response.data;
		} catch (err) {
			throw formatHttpError(err);
		}
	},

	async post<T>(path: string, data: any, options?: HttpOptions): Promise<T> {
		try {
			const response = await axiosInstance.post<T>(path, data, {
				headers: {
					'Content-Type': 'application/json',
					...(options?.headers || {}),
				},
				params: options?.params,
			});
			return response.data;
		} catch (err) {
			throw formatHttpError(err);
		}
	},
};

function formatHttpError(error: any): Error {
	if (axios.isAxiosError(error)) {
		const message = error.response?.data?.message || error.message;
		return new Error(message);
	}
	return new Error('Unknown error occurred');
}

// Manager API client — talks to wp-themes-manager-api (the internal admin
// backend), a DIFFERENT origin from the runtime endpoint above. It reads public
// per-theme config only, so there is no Bearer token and no 401→logout
// interceptor (a manager outage must never sign the player out of the site).
const managerAxios = axios.create({
	baseURL: MANAGER_ENDPOINT,
	headers: {
		Accept: 'application/json',
	},
	timeout: 20000,
});

export const managerHttp = {
	async get<T>(path: string, options?: HttpOptions): Promise<T> {
		try {
			const response = await managerAxios.get<T>(path, {
				headers: options?.headers,
				params: options?.params,
			});
			return response.data;
		} catch (err) {
			throw formatHttpError(err);
		}
	},
};
